import { Box, Paper, Typography } from "@mui/material";
import { useEffect } from "react";
import Chart from "react-apexcharts";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/app-button/AppButton";
import AppTable from "../../components/app-table/AppTable";
import Iconify from "../../components/iconify/Iconify";
import { PAYMENT_METHODS } from "../../constants/enums";
import { getSalesPaymentSummary } from "../../reduxStore/slices/reportSlice";
import { formatCurrency } from "../../utils/currency";
import { useNotify } from "../../utils/snackbarUtils";

const PaymentMethodReport = ({ startDate, endDate }) => {
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { paymentSummary, loading: isReportLoading } = useSelector(
    (state) => state.report,
  );
  const storeId = user?.storeId;

  const fetchPaymentSummary = async () => {
    try {
      await dispatch(
        getSalesPaymentSummary({
          storeId,
          startDate: startDate,
          endDate: endDate,
        }),
      ).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  // Fetch stats on mount and when storeId changes
  useEffect(() => {
    if (storeId) {
      fetchPaymentSummary();
    }
  }, [storeId, dispatch, startDate, endDate]);

  const paymentOptionLabels = Object.values(PAYMENT_METHODS).map(
    (option) => option.label,
  );

  const paymentOptionData = Object.values(PAYMENT_METHODS).map((method) => {
    const summary = paymentSummary.find(
      (item) => item.payment_option_id === method.value,
    );
    return summary?.percentage || 0;
  });

  const options = { labels: paymentOptionLabels };
  const series = paymentOptionData;

  const tableHead = [
    { id: "paymentMethod", label: "Payment Method", align: "left" },
    { id: "orders", label: "Orders", align: "left" },
    { id: "sales", label: "Sales (₹)", align: "left" },
  ];

  const tableRows = paymentSummary.map((item) => ({
    paymentMethodId: item.payment_option_id,
    paymentMethod: item.payment_method,
    orders: item.total_orders,
    sales: formatCurrency(item.total_amount),
  }));

  const chartOptions = {
    chart: { type: "pie" },
    labels: options.labels,
    legend: { position: "bottom" },
    title: {
      text: "Payment Method Trend",
      align: "left",
      style: { fontSize: "14px", fontWeight: "bold" },
    },
    tooltip: {
      y: {
        formatter: (val) => `${val}%`,
      },
    },
  };

  const handlePaymentExport = () => {
    const csvHeaders = ["Payment Method", "Orders", "Sales (₹)"];
    const csvRows = tableRows.map((row) => [
      row.paymentMethod,
      row.orders,
      row.sales,
    ]);
    const csvContent = [csvHeaders, ...csvRows]
      .map((e) => e.join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `payment_method_report_${Date.now()}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      showSuccess("Payment method report exported successfully.");
    }, 1000);
  };

  return (
    <>
      <Paper sx={{ p: 2, mb: 4 }}>
        <Chart options={chartOptions} series={series} type="pie" height={300} />
      </Paper>

      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" fontWeight={600}>
            Payment Method Details
          </Typography>
          <AppButton
            variant="contained"
            startIcon={<Iconify icon="ic:round-download" />}
            disabled={tableRows.length === 0}
            onClick={handlePaymentExport}
          >
            Export
          </AppButton>
        </Box>
        <AppTable
          tableHead={tableHead}
          tableRows={tableRows}
          listType="paymentMethodTables"
          searchPlaceholder="Search By Payment Method"
          searchField={"paymentMethod"}
        />
      </Box>
    </>
  );
};

export default PaymentMethodReport;
